import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Client } from "@xmpp/client"
import { JID } from "@xmpp/jid"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { SendHorizonal, Paperclip, X, Loader2, FileText } from "lucide-react"
import { Spinner } from "@/components/ui/spinner"
import { uploadToS3 } from "@/components/analyzeOrderOnPage/uploadToS3"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { selectChat } from "@/reducers/chatsSliceSelectors"
import { selectMessagesFromChat } from "@/reducers/messagesSliceSelectors"
import { sendMessage as sendMessageThunk } from "@/reducers/chatsSliceThunks"
import { AIChatView } from "@/components/ai-chat/AIChatView"
import { TransportBriefDialog } from "@/components/ai-chat/TransportBriefDialog"
import MapWidget from "@/components/slickAI/MapWidget"
import { selectAiWidgetMapCount } from "@/reducers/aiWidgetMapSlice"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import type { Chat } from "@/reducers/chatsSlice"

type AnonymousChatProps = {
  roomJid: string
  client: Client
  myJid: JID
  onBeforeSend?: () => boolean // return true to block the send
  onRouteLink?: (routeId: string) => void
}

const ROUTE_LINK_PATTERN =
  /^https?:\/\/(?:app\.slickshift\.ai|app\.trickswift\.eu|localhost:\d+)\/slick-ai\/?\?routeId=(.+)/

function extractRouteId(href: string): string | null {
  const match = href.match(ROUTE_LINK_PATTERN)
  return match ? match[1] : null
}

const EMPTY_MESSAGES: never[] = []

const AnonymousChat = ({
  roomJid,
  client,
  myJid,
  onBeforeSend,
  onRouteLink,
}: AnonymousChatProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const chat = useAppSelector(selectChat(roomJid))
  const messagesSelector = useMemo(
    () => selectMessagesFromChat(roomJid),
    [roomJid],
  )
  const reduxMessages = useAppSelector(messagesSelector) ?? EMPTY_MESSAGES

  const [inputValue, setInputValue] = useState("")
  const [pendingFile, setPendingFile] = useState<{
    file: File
    previewUrl: string
  } | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const prevAiMessageCountRef = useRef(0)
  const pendingFileRef = useRef(pendingFile)
  pendingFileRef.current = pendingFile

  const isConnected = chat != null

  useEffect(() => {
    return () => {
      if (pendingFileRef.current) {
        URL.revokeObjectURL(pendingFileRef.current.previewUrl)
      }
    }
  }, [])

  useEffect(() => {
    if (isConnected) {
      window.analytics?.track("Anonymous_ChatJoined", { roomJid })
    }
  }, [isConnected, roomJid])

  useEffect(() => {
    const aiMessageCount = reduxMessages.filter((m) => {
      // Heuristic mirrors logged-in flow: groupchat from agent resource
      const from = m.from || ""
      return /\/(assistant|transchat|slickai)$/.test(from)
    }).length
    if (aiMessageCount > prevAiMessageCountRef.current) {
      window.analytics?.track("Anonymous_ChatAIResponse", { roomJid })
    }
    prevAiMessageCountRef.current = aiMessageCount
  }, [reduxMessages, roomJid])

  const handleFileSelect = useCallback(
    (file: File) => {
      if (pendingFile) {
        URL.revokeObjectURL(pendingFile.previewUrl)
      }
      setPendingFile({ file, previewUrl: URL.createObjectURL(file) })
    },
    [pendingFile],
  )

  const handleRemoveFile = useCallback(() => {
    if (pendingFile) {
      URL.revokeObjectURL(pendingFile.previewUrl)
    }
    setPendingFile(null)
  }, [pendingFile])

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (e.clipboardData.files && e.clipboardData.files.length > 0) {
        e.preventDefault()
        const file = e.clipboardData.files[0]
        handleFileSelect(file)
        window.analytics?.track("Anonymous_ChatFileAttached", {
          roomJid,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          source: "paste",
        })
      }
    },
    [handleFileSelect, roomJid],
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        const file = files[0]
        handleFileSelect(file)
        window.analytics?.track("Anonymous_ChatFileAttached", {
          roomJid,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          source: "picker",
        })
      }
      e.target.value = ""
    },
    [handleFileSelect, roomJid],
  )

  const sendBody = useCallback(
    (body: string) => {
      if (!chat) return
      dispatch(sendMessageThunk(body, client, myJid.toString(), chat))
    },
    [chat, client, dispatch, myJid],
  )

  const handleSend = useCallback(async () => {
    const text = inputValue.trim()
    const hasText = text.length > 0
    const hasFile = pendingFile !== null

    if (!hasText && !hasFile) return
    if (onBeforeSend?.()) return // lead capture

    let fileUrl: string | null = null
    if (hasFile && pendingFile) {
      setIsUploading(true)
      try {
        const { getUrl } = await uploadToS3(pendingFile.file)
        fileUrl = getUrl
        window.analytics?.track("Anonymous_ChatFileSent", {
          roomJid,
          fileName: pendingFile.file.name,
          fileType: pendingFile.file.type,
          fileSize: pendingFile.file.size,
        })
      } catch (err) {
        console.error("File upload failed:", err)
        window.analytics?.track("Anonymous_ChatFileUploadFailed", {
          roomJid,
          fileName: pendingFile.file.name,
          fileType: pendingFile.file.type,
          error: err instanceof Error ? err.message : String(err),
        })
      } finally {
        URL.revokeObjectURL(pendingFile.previewUrl)
        setPendingFile(null)
        setIsUploading(false)
      }
    }

    const parts: string[] = []
    if (hasText) parts.push(text)
    if (fileUrl) parts.push(fileUrl)

    if (parts.length > 0) {
      const body = parts.join("\n")
      sendBody(body)
      setInputValue("")
      window.analytics?.track("Anonymous_ChatMessageSent", {
        roomJid,
        messageLength: body.length,
        hasFile: !!fileUrl,
      })
    }

    setTimeout(() => textareaRef.current?.focus(), 0)
  }, [inputValue, pendingFile, sendBody, roomJid, onBeforeSend])

  const handleSelectPrompt = useCallback((text: string) => {
    setInputValue(text)
    setTimeout(() => textareaRef.current?.focus(), 0)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  // Input markup — owned by the parent so all input state stays here, but
  // rendered inside AnonymousChatBody's chat panel so it sits below the chat
  // (not under the map widget) when the layout splits. Matches the
  // authenticated app where ChatHistory/ChatInput live inside the chat
  // ResizablePanel.
  const inputSlot = (
    <div className="border-t bg-white p-3">
      {pendingFile && (
        <div className="mb-2 flex items-center gap-2 rounded-md border bg-gray-50 p-2">
          {pendingFile.file.type.startsWith("image/") ? (
            <img
              src={pendingFile.previewUrl}
              alt={pendingFile.file.name}
              className="h-12 w-12 rounded object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded bg-gray-200">
              <FileText className="h-6 w-6 text-gray-500" />
            </div>
          )}
          <span className="min-w-0 flex-1 truncate text-sm text-gray-700">
            {pendingFile.file.name}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={handleRemoveFile}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={!isConnected || isUploading}
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        <Textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={t("planOrder.anonymousChat.placeholder")}
          className="max-h-[120px] min-h-[40px] resize-none"
          rows={1}
          disabled={!isConnected || isUploading}
        />
        <Button
          onClick={handleSend}
          disabled={
            !isConnected || isUploading || (!inputValue.trim() && !pendingFile)
          }
          size="icon"
          className="h-10 w-10 shrink-0 bg-blue-600 hover:bg-blue-700"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <SendHorizonal className="h-4 w-4" />
          )}
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  )

  return (
    <div className="flex h-screen flex-col bg-[#FAFAFA]">
      {/* Header */}
      <div className="flex items-center gap-2 border-b bg-white px-4 py-3">
        <img src="/slickAvatar.svg" className="h-7 w-7" alt="Slick AI" />
        <span className="font-medium">Slick AI</span>
      </div>

      {/* Messages + input area */}
      {!isConnected ? (
        <div className="flex h-full flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <Spinner size="sm" />
            <p className="text-sm text-muted-foreground">
              {t("planOrder.anonymousChat.connecting")}
            </p>
          </div>
        </div>
      ) : (
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onClickCapture={(event) => {
            if (!onRouteLink) return
            const anchor = (event.target as HTMLElement).closest("a")
            const href = anchor?.getAttribute("href")
            if (!href) return
            const routeId = extractRouteId(href)
            if (routeId) {
              event.preventDefault()
              onRouteLink(routeId)
            }
          }}
        >
          {/* isRoomSyncFinished={false} disables MAM history fetch — anonymous chats have no history.
              Welcome message arrives from the agent via MUC-SUB presence push (anonymousMode), so
              no static WelcomeMessage component lives here anymore.

              When the agent ships an ai-widget-map, the body splits horizontally:
              the chat (with its input docked at the bottom) on the left,
              MapWidget on the right — same shape as authenticated app's
              ChatHistory + SlickAiSidebar layout. */}
          <AnonymousChatBody
            chat={chat}
            sendBody={sendBody}
            roomJid={roomJid}
            inputSlot={inputSlot}
            onSelectPrompt={handleSelectPrompt}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Renders the chat transcript (with the chat input docked at its bottom) and,
 * when the agent has shipped any `<ai-widget-map>` stanzas for this room, a
 * resizable map panel beside it. Same MapWidget the authenticated app uses in
 * `routes/Root.tsx` (title bar, prev/next, close) — we just don't have a
 * workflow list to share the column with here, so the map gets the whole
 * right pane. The input always lives inside the chat panel, never spanning
 * across the map.
 */
type AnonymousChatBodyProps = {
  chat: Chat | undefined
  sendBody: (text: string) => void
  roomJid: string
  inputSlot: React.ReactNode
  onSelectPrompt: (text: string) => void
}
const AnonymousChatBody = ({
  chat,
  sendBody,
  roomJid,
  inputSlot,
  onSelectPrompt,
}: AnonymousChatBodyProps) => {
  const hasMaps = useAppSelector(selectAiWidgetMapCount(roomJid)) > 0
  if (!chat) return null
  // The chat column = transcript + input docked at the bottom. AIChatView's
  // internal `flex-1` needs a `min-h-0` flex parent to clip overflow; the
  // input has natural intrinsic height and stays pinned at the bottom.
  const chatColumn = (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <AIChatView
        chat={chat}
        isRoomSyncFinished={false}
        onSendMessage={sendBody}
        onSelectPrompt={onSelectPrompt}
      />
      {inputSlot}
      <TransportBriefDialog />
    </div>
  )
  if (!hasMaps) return chatColumn
  return (
    <ResizablePanelGroup direction="horizontal">
      <ResizablePanel defaultSize={55} minSize={25}>
        {chatColumn}
      </ResizablePanel>
      {/* Match the authenticated app's invisible-until-hovered handle (no grip
          dot, transparent line that highlights on hover/drag). */}
      <ResizableHandle className="bg-transparent transition-colors hover:bg-border data-[resize-handle-state=drag]:bg-border data-[resize-handle-state=hover]:bg-border" />
      <ResizablePanel defaultSize={45} minSize={25}>
        <div className="flex h-full min-h-0 flex-col">
          <MapWidget chatJid={roomJid} />
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}

export default AnonymousChat
